<template>
  <!--
    การจัดการกรอบอัตราพนักงานราชการโดยส่วนกลาง (govem_frame_admin.php) — ใช้ในหน้า Admin เท่านั้น
    เรียกผ่าน ref: openAdd(collegeCode?) · openCut(row) · openMove(row) · openLog(collegeCode?)
    emit "changed" เมื่อข้อมูลกรอบเปลี่ยน (หน้าแม่โหลดรายการใหม่)
  -->
  <div>
    <!-- ===== เพิ่มกรอบอัตรา ===== -->
    <v-dialog v-model="addDialog" max-width="720" persistent>
      <v-card>
        <v-toolbar dense dark flat color="success darken-1">
          <v-icon left>mdi-briefcase-plus</v-icon>
          <v-toolbar-title class="subtitle-1">เพิ่มกรอบอัตราพนักงานราชการ (ส่วนกลาง)</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-alert type="info" dense text class="caption">
            สร้างตำแหน่งว่างใหม่ในกรอบอัตราของหน่วยงาน (ตามหนังสืออนุมัติอัตรา) — เลขที่ตำแหน่งต้องไม่ซ้ำทั้งระบบ
            หลังเพิ่มแล้วหน่วยงานบรรจุ/รับย้ายคนลงตำแหน่งนี้ได้
          </v-alert>
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                v-model="addForm.college_code"
                :items="unitItems"
                item-text="label"
                item-value="user_code"
                label="หน่วยงานที่ได้รับอัตรา *"
                outlined
                dense
                :loading="loadingUnits"
              />
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field v-model.trim="addForm.id_position" label="เลขที่ตำแหน่ง *" outlined dense
                :hint="nextPosition ? 'เลขถัดไปที่ว่าง: ' + nextPosition : ''" persistent-hint>
                <template v-slot:append>
                  <v-btn v-if="nextPosition" x-small text color="primary" @click="addForm.id_position = nextPosition">ใช้เลขนี้</v-btn>
                </template>
              </v-text-field>
            </v-col>
            <v-col cols="12" md="8">
              <v-combobox v-model="addForm.name_postion" :items="positionNames" label="ชื่อตำแหน่ง *" outlined dense />
            </v-col>
            <v-col cols="12" md="4">
              <v-select v-model="addForm.name_type_group" :items="groups" label="กลุ่มงาน *" outlined dense />
            </v-col>
            <v-col cols="12" md="5">
              <v-text-field v-model.trim="addForm.order_no" label="เลขที่หนังสืออนุมัติอัตรา *" outlined dense />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field v-model="addForm.order_date" type="date" label="ลงวันที่" outlined dense />
            </v-col>
            <v-col cols="12">
              <v-text-field v-model="addForm.note" label="หมายเหตุ" outlined dense hide-details />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="saving" @click="addDialog = false">ยกเลิก</v-btn>
          <v-spacer />
          <v-btn color="success" :loading="saving" @click="submit('add_position', addForm)">เพิ่มกรอบอัตรา</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== ตัดโอนตำแหน่ง ===== -->
    <v-dialog v-model="cutDialog" max-width="720" persistent>
      <v-card v-if="row">
        <v-toolbar dense dark flat color="deep-orange darken-1">
          <v-icon left>mdi-swap-horizontal-bold</v-icon>
          <v-toolbar-title class="subtitle-1">ตัดโอนตำแหน่ง (ส่วนกลาง)</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-simple-table dense class="mb-3">
            <tbody>
              <tr><td width="160">เลขที่ตำแหน่ง</td><td><strong>{{ row.id_position }}</strong> · {{ row.name_postion }}</td></tr>
              <tr><td>สังกัดปัจจุบัน</td><td>{{ unitName(row.college_code) }}</td></tr>
              <tr><td>ผู้ครองตำแหน่ง</td><td>{{ personText(row) }}</td></tr>
            </tbody>
          </v-simple-table>
          <v-alert :type="hasPerson(row) ? 'warning' : 'info'" dense text class="caption">
            ตำแหน่งเลขที่ {{ row.id_position }} จะย้ายไปอยู่ในกรอบอัตราของหน่วยงานปลายทาง
            <span v-if="hasPerson(row)"><strong>พร้อมผู้ครองตำแหน่ง</strong> ({{ personText(row) }}) — ทะเบียนประวัติย้ายสังกัดตาม และบันทึกประวัติการย้าย</span>
            <span v-else>(ตำแหน่งว่าง)</span>
            · คำขอย้ายที่ค้างอยู่ซึ่งอ้างตำแหน่งนี้จะถูกยกเลิก
          </v-alert>
          <v-row dense>
            <v-col cols="12">
              <v-autocomplete
                v-model="cutForm.to_college"
                :items="unitItems.filter(u => u.user_code !== row.college_code)"
                item-text="label"
                item-value="user_code"
                label="หน่วยงานปลายทาง *"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="5"><v-text-field v-model.trim="cutForm.order_no" label="เลขที่คำสั่ง *" outlined dense /></v-col>
            <v-col cols="12" md="3"><v-text-field v-model="cutForm.order_date" type="date" label="ลงวันที่" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="cutForm.effective_date" type="date" label="วันที่มีผล *" outlined dense /></v-col>
            <v-col cols="12"><v-text-field v-model="cutForm.note" label="เหตุผล/หมายเหตุ" outlined dense hide-details /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="saving" @click="cutDialog = false">ยกเลิก</v-btn>
          <v-spacer />
          <v-btn color="deep-orange darken-1" dark :loading="saving" @click="submit('cut_transfer', { ...cutForm, id_no: row.id_no })">
            ยืนยันตัดโอนตำแหน่ง
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== ย้าย (ส่วนกลางดำเนินการ) ===== -->
    <v-dialog v-model="moveDialog" max-width="760" persistent>
      <v-card v-if="row">
        <v-toolbar dense dark flat color="indigo">
          <v-icon left>mdi-account-arrow-right</v-icon>
          <v-toolbar-title class="subtitle-1">ย้ายพนักงานราชการ (ส่วนกลาง)</v-toolbar-title>
        </v-toolbar>
        <v-card-text class="pt-4">
          <v-simple-table dense class="mb-3">
            <tbody>
              <tr><td width="160">ผู้ย้าย</td><td><strong>{{ personText(row) }}</strong></td></tr>
              <tr><td>ตำแหน่งปัจจุบัน</td><td>เลขที่ {{ row.id_position }} · {{ row.name_postion }} · {{ unitName(row.college_code) }}</td></tr>
            </tbody>
          </v-simple-table>
          <v-alert type="info" dense text class="caption">
            ผู้ย้ายลงตำแหน่งว่างของหน่วยงานปลายทาง ตำแหน่งเดิม (เลขที่ {{ row.id_position }}) เป็นตำแหน่งว่างของหน่วยงานเดิม
            · ถ้าเริ่มปฏิบัติงานที่ใหม่ 1 ต.ค. ให้ระบุวันที่มีผล 1 ต.ค. — การเลื่อนค่าตอบแทนจะคำนวณที่หน่วยงานเดิม
          </v-alert>
          <v-row dense>
            <v-col cols="12" md="6">
              <v-autocomplete
                v-model="moveForm.to_college"
                :items="unitItems.filter(u => u.user_code !== row.college_code)"
                item-text="label"
                item-value="user_code"
                label="หน่วยงานปลายทาง *"
                outlined
                dense
                @change="loadVacancies"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-select
                v-model="moveForm.target_id_no"
                :items="vacancies"
                :item-text="v => `เลขที่ ${v.id_position} · ${v.name_postion || '-'}`"
                item-value="id_no"
                label="ตำแหน่งว่างปลายทาง *"
                outlined
                dense
                :loading="loadingVacancies"
                :no-data-text="moveForm.to_college ? 'หน่วยงานนี้ไม่มีตำแหน่งว่าง — ใช้ตัดโอนตำแหน่ง หรือเพิ่มกรอบอัตราก่อน' : 'เลือกหน่วยงานก่อน'"
              />
            </v-col>
            <v-col cols="12" md="5"><v-text-field v-model.trim="moveForm.order_no" label="เลขที่คำสั่ง *" outlined dense /></v-col>
            <v-col cols="12" md="3"><v-text-field v-model="moveForm.order_date" type="date" label="ลงวันที่" outlined dense /></v-col>
            <v-col cols="12" md="4"><v-text-field v-model="moveForm.effective_date" type="date" label="วันที่มีผล *" outlined dense /></v-col>
            <v-col cols="12"><v-text-field v-model="moveForm.note" label="เหตุผล/หมายเหตุ" outlined dense hide-details /></v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-btn text :disabled="saving" @click="moveDialog = false">ยกเลิก</v-btn>
          <v-spacer />
          <v-btn color="indigo" dark :loading="saving"
            @click="submit('move_person', { ...moveForm, source_id_no: row.id_no })">ยืนยันการย้าย</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ===== ประวัติการจัดการกรอบ ===== -->
    <v-dialog v-model="logDialog" max-width="1100" scrollable>
      <v-card>
        <v-toolbar dense dark flat color="blue-grey darken-1" class="flex-grow-0">
          <v-icon left>mdi-history</v-icon>
          <v-toolbar-title class="subtitle-1">ประวัติการจัดการกรอบอัตรา (ส่วนกลาง)</v-toolbar-title>
          <v-spacer />
          <v-btn icon @click="logDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </v-toolbar>
        <v-card-text class="pt-3">
          <v-data-table :headers="logHeaders" :items="log" :loading="loadingLog" dense :items-per-page="20" :footer-props="{ 'items-per-page-options': [20, 50, 100, -1] }">
            <template v-slot:[`item.action`]="{ item }">
              <v-chip x-small dark :color="actionColor(item.action)">{{ actionText(item.action) }}</v-chip>
            </template>
            <template v-slot:[`item.position`]="{ item }">
              {{ item.from_position && item.from_position !== item.to_position ? item.from_position + " → " : "" }}{{ item.to_position }}
              <div class="caption grey--text">{{ item.name_postion }}</div>
            </template>
            <template v-slot:[`item.units`]="{ item }">
              <span v-if="item.from_college">{{ item.from_name }} → </span>{{ item.to_name }}
            </template>
            <template v-slot:[`item.order_no`]="{ item }">
              {{ item.order_no }}
              <div class="caption grey--text">
                {{ item.order_date ? "ลว. " + item.order_date : "" }}{{ item.effective_date ? " · มีผล " + item.effective_date : "" }}
              </div>
              <div v-if="item.note" class="caption">{{ item.note }}</div>
            </template>
            <template v-slot:[`item.acted_at`]="{ item }">
              {{ item.acted_at }}<div class="caption grey--text">{{ item.acted_by_name }}</div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snack.show" :color="snack.color" top :timeout="5000">{{ snack.text }}</v-snackbar>
  </div>
</template>

<script>
const API = "govem_frame_admin.php";
const today = () => new Date().toISOString().slice(0, 10);

export default {
  name: "GovemFrameActions",
  data() {
    return {
      ApiKey: "HRvec2021",
      units: [],
      loadingUnits: false,
      saving: false,
      row: null,
      addDialog: false,
      cutDialog: false,
      moveDialog: false,
      logDialog: false,
      nextPosition: "",
      addForm: {},
      cutForm: {},
      moveForm: {},
      vacancies: [],
      loadingVacancies: false,
      log: [],
      loadingLog: false,
      groups: ["บริหารทั่วไป", "บริการ", "วิชาชีพเฉพาะ", "เชี่ยวชาญเฉพาะ", "เทคนิค"],
      positionNames: [
        "พนักงานบริหารทั่วไป (ครู)",
        "พนักงานบริหารทั่วไป (ด้านบริหารงานทั่วไป)",
        "พนักงานบริหารทั่วไป (ด้านการเงินและบัญชี)",
        "พนักงานบริหารทั่วไป (ด้านพัสดุ)",
        "พนักงานบริหารทั่วไป (ด้านสารสนเทศ)",
        "พนักงานบริหารทั่วไป (ด้านวิชาการศึกษา)",
        "พนักงานบริหารทั่วไป (ด้านบริหารงานบุคคล)",
        "พนักงานบริการ (ด้านบริหารงานทั่วไป)",
        "พนักงานบริการ (ด้านบริการ)",
        "พนักงานบริการ (ด้านสารสนเทศ)",
        "พนักงานบริการ (ด้านพัสดุ)"
      ],
      snack: { show: false, text: "", color: "success" },
      logHeaders: [
        { text: "ประเภท", value: "action", width: 120 },
        { text: "ผู้ครอง", value: "full_name" },
        { text: "เลขที่ตำแหน่ง", value: "position", sortable: false },
        { text: "หน่วยงาน", value: "units", sortable: false },
        { text: "คำสั่ง", value: "order_no" },
        { text: "ดำเนินการเมื่อ", value: "acted_at", width: 170 }
      ]
    };
  },
  computed: {
    unitItems() {
      const type = { B: "สถานศึกษา", C: "สถาบัน", V: "ส่วนกลาง" };
      return this.units.map(u => ({
        ...u,
        label: `${u.unit_name} (${u.user_code}) · ${type[u.user_status] || u.user_status} · กรอบ ${u.positions} ว่าง ${u.vacant}`
      }));
    }
  },
  methods: {
    async api(payload) {
      try {
        const res = await this.$http.post(API, { ApiKey: this.ApiKey, ...payload });
        if (!res.data || res.data.status !== true) throw new Error((res.data && res.data.message) || "ไม่ได้รับข้อมูล");
        return res.data;
      } catch (e) {
        const msg = e.response && e.response.data && e.response.data.message;
        throw new Error(msg || e.message);
      }
    },
    notify(text, color = "success") {
      this.snack = { show: true, text, color };
    },
    async ensureUnits(force = false) {
      if (this.units.length && !force) return;
      this.loadingUnits = true;
      try {
        this.units = (await this.api({ action: "units" })).units;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.loadingUnits = false;
      }
    },
    unitName(code) {
      const u = this.units.find(x => x.user_code === code);
      return u ? `${u.unit_name} (${code})` : code;
    },
    hasPerson(row) {
      return row && String(row.id_card || "0") !== "0";
    },
    personText(row) {
      if (!this.hasPerson(row)) return "ตำแหน่งว่าง";
      return `${row.title_s || ""}${row.frist_name || ""} ${row.last_name || ""}`.trim() + ` (${row.id_card})`;
    },
    actionText(a) {
      return { add_position: "เพิ่มกรอบอัตรา", cut_transfer: "ตัดโอนตำแหน่ง", move_person: "ย้าย (ส่วนกลาง)" }[a] || a;
    },
    actionColor(a) {
      return { add_position: "success", cut_transfer: "deep-orange", move_person: "indigo" }[a] || "grey";
    },
    async openAdd(collegeCode = "") {
      await this.ensureUnits();
      this.addForm = { college_code: collegeCode || "", id_position: "", name_postion: "", name_type_group: "บริหารทั่วไป",
        order_no: "", order_date: today(), note: "" };
      this.addDialog = true;
      try {
        this.nextPosition = (await this.api({ action: "next_position" })).next;
      } catch (e) {
        this.nextPosition = "";
      }
    },
    async openCut(row) {
      await this.ensureUnits();
      this.row = row;
      this.cutForm = { to_college: "", order_no: "", order_date: today(), effective_date: today(), note: "" };
      this.cutDialog = true;
    },
    async openMove(row) {
      if (!this.hasPerson(row)) {
        this.notify("ตำแหน่งว่าง — ใช้ \"ตัดโอนตำแหน่ง\" แทน", "warning");
        return;
      }
      await this.ensureUnits();
      this.row = row;
      this.vacancies = [];
      this.moveForm = { to_college: "", target_id_no: null, order_no: "", order_date: today(), effective_date: today(), note: "" };
      this.moveDialog = true;
    },
    async loadVacancies() {
      this.moveForm.target_id_no = null;
      this.vacancies = [];
      if (!this.moveForm.to_college) return;
      this.loadingVacancies = true;
      try {
        this.vacancies = (await this.api({ action: "positions", college_code: this.moveForm.to_college, vacant_only: 1 })).positions;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.loadingVacancies = false;
      }
    },
    async openLog(collegeCode = "") {
      await this.ensureUnits();
      this.logDialog = true;
      this.loadingLog = true;
      try {
        this.log = (await this.api({ action: "log", college_code: collegeCode || undefined })).log;
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.loadingLog = false;
      }
    },
    async submit(action, form) {
      const label = this.actionText(action);
      if (!confirm(`ยืนยัน "${label}"? การดำเนินการนี้เปลี่ยนกรอบอัตรากำลังจริงและบันทึกประวัติ`)) return;
      this.saving = true;
      try {
        await this.api({ action, ...form });
        this.addDialog = this.cutDialog = this.moveDialog = false;
        this.notify(`${label} เรียบร้อย`);
        await this.ensureUnits(true);
        this.$emit("changed", action);
      } catch (e) {
        this.notify(e.message, "error");
      } finally {
        this.saving = false;
      }
    }
  }
};
</script>
